import api from '@/api/api';
import { useApiGet } from '@/api/apiRequests';
import { PageLoader } from '@/components/PageLoader';
import { AxiosError } from 'axios';
import { FC, useState, useEffect, useRef } from 'react';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { toast } from 'react-toastify';
import { EditIcon } from 'lucide-react';

interface PromptItem {
  id: number;
  query: string;
  hsou: boolean;
  charakter: string;
  // biome-ignore lint/style/useNamingConvention: API response
  is_active?: boolean;
  // biome-ignore lint/style/useNamingConvention: API response
  prompt_type: string;
  // biome-ignore lint/style/useNamingConvention: API response
  version_name?: string | null;
  // biome-ignore lint/style/useNamingConvention: API response
  created_at: string;
  // biome-ignore lint/style/useNamingConvention: API response
  updated_at: string;
  // biome-ignore lint/style/useNamingConvention: API response
  deleted_at: string | null;
}

export const PromptEngineering: FC = () => {
  const [selectedPromptType, setSelectedPromptType] = useState<string | null>(() => {
    return localStorage.getItem('promptEngineering_promptType');
  });
  const [selectedCharakter, setSelectedCharakter] = useState<string | null>(() => {
    return localStorage.getItem('promptEngineering_charakter');
  });
  const [hsou, setHsou] = useState<boolean>(() => {
    return localStorage.getItem('promptEngineering_hsou') === 'true';
  });
  const [prompts, setPrompts] = useState<PromptItem[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptItem | null>(null);
  const [loadingPrompts, setLoadingPrompts] = useState<boolean>(false);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [loadingActivate, setLoadingActivate] = useState<boolean>(false);
  const [createDialogVisible, setCreateDialogVisible] = useState<boolean>(false);
  const [newPromptQuery, setNewPromptQuery] = useState<string>('');
  const [newPromptVersionName, setNewPromptVersionName] = useState<string>('');
  const [newPromptIsActive, setNewPromptIsActive] = useState<boolean>(false);
  const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
  const [editedQuery, setEditedQuery] = useState<string>('');
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [editedVersionName, setEditedVersionName] = useState<string>('');
  const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

  const isInitialMount = useRef(true);

  // Clear prompts when selection changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPrompts([]);
    setSelectedPrompt(null);
  }, [selectedPromptType, selectedCharakter, hsou]);

  // Sync editedQuery and editedVersionName with selectedPrompt
  useEffect(() => {
    setEditedQuery(selectedPrompt?.query || '');
    setEditedVersionName(selectedPrompt?.version_name || '');
    setIsEditingName(false);
  }, [selectedPrompt]);

  // Save selections to localStorage
  useEffect(() => {
    if (selectedPromptType) {
      localStorage.setItem('promptEngineering_promptType', selectedPromptType);
    } else {
      localStorage.removeItem('promptEngineering_promptType');
    }
  }, [selectedPromptType]);

  useEffect(() => {
    if (selectedCharakter) {
      localStorage.setItem('promptEngineering_charakter', selectedCharakter);
    } else {
      localStorage.removeItem('promptEngineering_charakter');
    }
  }, [selectedCharakter]);

  useEffect(() => {
    localStorage.setItem('promptEngineering_hsou', String(hsou));
  }, [hsou]);

  const { data: promptTypes, isLoading: promptTypesLoading } = useApiGet<string[], AxiosError>(
    ['promptTypes'],
    async () => {
      const response = await api.get('/prompt/prompt-types');
      return response.data;
    },
    { enabled: true }
  );

  const { data: characters, isLoading: charactersLoading } = useApiGet<string[], AxiosError>(
    ['characters'],
    async () => {
      const response = await api.get('/prompt/characters');
      return response.data;
    },
    { enabled: true }
  );

  const isBasicType = selectedPromptType === 'basic';
  const isInitialLoading = promptTypesLoading || charactersLoading;

  const canSubmit = selectedPromptType && (!isBasicType || (isBasicType && selectedCharakter));

  // Auto-load prompts on page load if prompt type is saved
  useEffect(() => {
    if (!isInitialLoading && canSubmit && prompts.length === 0) {
      handleShowPromptsAuto();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialLoading]);

  const handleShowPromptsAuto = async () => {
    if (!selectedPromptType) return;

    setLoadingPrompts(true);
    setPrompts([]);
    setSelectedPrompt(null);

    try {
      const params = new URLSearchParams({
        page: '1',
        order: 'desc',
        // biome-ignore lint/style/useNamingConvention: API param
        prompt_type: selectedPromptType,
      });

      const isBasic = selectedPromptType === 'basic';
      if (isBasic && selectedCharakter) {
        params.append('character_value', selectedCharakter);
        params.append('hsou_value', hsou ? 'True' : 'False');
      }

      const response = await api.get<PromptItem[]>(`/prompt/get-all?${params.toString()}`);
      const items = response.data || [];
      setPrompts(items);
      if (items.length > 0) {
        const activePrompt = items.find(p => p.is_active);
        setSelectedPrompt(activePrompt || items[0]);
      }
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to fetch prompts.');
      }
      console.error('Error fetching prompts:', error);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const handleShowPrompts = async () => {
    if (!selectedPromptType) return;

    setLoadingPrompts(true);
    setPrompts([]);
    setSelectedPrompt(null);

    try {
      const params = new URLSearchParams({
        page: '1',
        order: 'desc',
        // biome-ignore lint/style/useNamingConvention: API param
        prompt_type: selectedPromptType,
      });

      if (isBasicType && selectedCharakter) {
        params.append('character_value', selectedCharakter);
        params.append('hsou_value', hsou ? 'True' : 'False');
      }

      const response = await api.get<PromptItem[]>(`/prompt/get-all?${params.toString()}`);
      const items = response.data || [];
      setPrompts(items);
      if (items.length > 0) {
        const activePrompt = items.find(p => p.is_active);
        setSelectedPrompt(activePrompt || items[0]);
      }
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to fetch prompts.');
      }
      console.error('Error fetching prompts:', error);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const handleActivate = async () => {
    if (!selectedPrompt?.id) return;

    setLoadingActivate(true);
    try {
      await api.post(`/prompt/activate/${selectedPrompt.id}`);
      toast.success('Prompt byl úspěšně aktivován.');
      
      // Update prompts list to reflect new active status
      setPrompts(prompts.map(p => ({
        ...p,
        is_active: p.id === selectedPrompt.id
      })));
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Nepodařilo se aktivovat prompt.');
      }
      console.error('Error activating prompt:', error);
    } finally {
      setLoadingActivate(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPrompt?.id || !editedQuery) return;

    setLoadingUpdate(true);
    try {
      await api.put(`/prompt/update/${selectedPrompt.id}`, {
        query: editedQuery,
        // biome-ignore lint/style/useNamingConvention: API param
        version_name: selectedPrompt.version_name || '',
      });
      toast.success('Prompt was successfully updated.');
      
      // Update the prompt in the list
      setPrompts(prompts.map(p => 
        p.id === selectedPrompt.id 
          ? { ...p, query: editedQuery, updated_at: new Date().toISOString() }
          : p
      ));
      setSelectedPrompt({ ...selectedPrompt, query: editedQuery, updated_at: new Date().toISOString() });
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to update prompt.');
      }
      console.error('Error updating prompt:', error);
    } finally {
      setLoadingUpdate(false);
    }
  };

  const handleRename = async () => {
    if (!selectedPrompt?.id) return;

    try {
      await api.put(`/prompt/update/${selectedPrompt.id}`, {
        query: selectedPrompt.query,
        // biome-ignore lint/style/useNamingConvention: API param
        version_name: editedVersionName,
      });
      toast.success('Prompt was successfully renamed.');
      
      // Update the prompt in the list
      setPrompts(prompts.map(p => 
        p.id === selectedPrompt.id 
          ? { ...p, version_name: editedVersionName, updated_at: new Date().toISOString() }
          : p
      ));
      setSelectedPrompt({ ...selectedPrompt, version_name: editedVersionName, updated_at: new Date().toISOString() });
      setIsEditingName(false);
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to rename prompt.');
      }
      console.error('Error renaming prompt:', error);
    }
  };

  const handleDelete = async () => {
    if (!selectedPrompt?.id) return;

    setLoadingDelete(true);
    try {
      await api.delete(`/prompt/soft-delete/${selectedPrompt.id}`);
      toast.success('Prompt was successfully deleted.');
      
      // Remove the prompt from the list
      const updatedPrompts = prompts.filter(p => p.id !== selectedPrompt.id);
      setPrompts(updatedPrompts);
      setSelectedPrompt(updatedPrompts.length > 0 ? updatedPrompts[0] : null);
      setDeleteDialogVisible(false);
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to delete prompt.');
      }
      console.error('Error deleting prompt:', error);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleCreate = async () => {
    if (!newPromptQuery || !selectedPromptType) return;

    setLoadingCreate(true);
    try {
      await api.post('/prompt/create', {
        query: newPromptQuery,
        hsou: isBasicType ? hsou : null,
        charakter: isBasicType ? selectedCharakter : null,
        // biome-ignore lint/style/useNamingConvention: API param
        prompt_type: selectedPromptType,
        // biome-ignore lint/style/useNamingConvention: API param
        version_name: newPromptVersionName,
        // biome-ignore lint/style/useNamingConvention: API param
        is_active: newPromptIsActive,
      });
      toast.success('Prompt byl úspěšně vytvořen.');
      setCreateDialogVisible(false);
      setNewPromptQuery('');
      setNewPromptVersionName('');
      setNewPromptIsActive(false);
      // Refresh the prompts list
      handleShowPrompts();
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Nepodařilo se vytvořit prompt.');
      }
      console.error('Error creating prompt:', error);
    } finally {
      setLoadingCreate(false);
    }
  };

  return (
    <>
      <h1 className={'text-2xl font-bold mb-8 text-white'}>Prompt Engineering</h1>
      <div className={'bg-white rounded-lg p-6 shadow-sm'}>
        <div className="flex flex-col gap-6 max-w-md">
          <div className="field">
            <label htmlFor="promptType" className="font-bold block mb-2">
              Prompt type
            </label>
            <Dropdown
              id="promptType"
              value={selectedPromptType}
              onChange={(e: DropdownChangeEvent) => setSelectedPromptType(e.value)}
              options={promptTypes || []}
              placeholder="Vyberte typ promptu"
              className="w-full"
              loading={promptTypesLoading}
            />
          </div>

          {isBasicType && (
            <>
              <div className="field">
                <label htmlFor="charakter" className="font-bold block mb-2">
                  Charakter
                </label>
                <Dropdown
                  id="charakter"
                  value={selectedCharakter}
                  onChange={(e: DropdownChangeEvent) => setSelectedCharakter(e.value)}
                  options={characters || []}
                  placeholder="Vyberte charakter"
                  className="w-full"
                  loading={charactersLoading}
                />
              </div>

              <div className="field">
                <div className="flex items-center gap-2">
                  <label htmlFor="hsou" className="font-bold cursor-pointer">HSOU</label>
                  <Checkbox
                    inputId="hsou"
                    checked={hsou}
                    onChange={(e: CheckboxChangeEvent) => setHsou(e.checked ?? false)}
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex justify-start">
            <Button
              label="Show"
              className="btn-primary"
              style={{ borderRadius: '6px' }}
              onClick={handleShowPrompts}
              disabled={!canSubmit}
              loading={loadingPrompts}
            />
          </div>
        </div>
      </div>

      {prompts.length > 0 && (
        <div className="bg-white rounded-lg p-6 shadow-sm mt-6">
          <div className="flex gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-4">
                {isEditingName ? (
                  <>
                    <span className="font-bold text-lg text-gray-700">Prompt name:</span>
                    <InputText
                      value={editedVersionName}
                      onChange={(e) => setEditedVersionName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setIsEditingName(false);
                          setEditedVersionName(selectedPrompt?.version_name || '');
                        } else if (e.key === 'Enter') {
                          handleRename();
                        }
                      }}
                      className="p-inputtext-sm"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="text-green-600 hover:text-green-800 transition-colors p-1"
                      onClick={handleRename}
                      title="Save"
                    >
                      ✓
                    </button>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-700 transition-colors p-1"
                      onClick={() => {
                        setIsEditingName(false);
                        setEditedVersionName(selectedPrompt?.version_name || '');
                      }}
                      title="Cancel"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="font-bold text-lg text-gray-700">
                      Prompt name: {selectedPrompt?.version_name || `Prompt #${selectedPrompt?.id}`}
                    </h3>
                    <Button
                      icon={<EditIcon size={16} />}
                      className="p-button-text p-button-sm"
                      onClick={() => setIsEditingName(true)}
                      title="Rename prompt"
                    />
                  </>
                )}
              </div>
              <InputTextarea
                value={editedQuery}
                onChange={(e) => setEditedQuery(e.target.value)}
                className="w-full h-[500px] font-mono text-sm"
                style={{ resize: 'none' }}
              />
              <div className="mt-4 flex gap-2">
                <Button
                  label="Activate"
                  icon="pi pi-check"
                  className="btn-primary"
                  style={{ borderRadius: '6px' }}
                  onClick={handleActivate}
                  disabled={!selectedPrompt || selectedPrompt?.is_active}
                  loading={loadingActivate}
                />
                <Button
                  label="Update"
                  icon="pi pi-save"
                  className="p-button-outlined"
                  onClick={handleUpdate}
                  disabled={!selectedPrompt || editedQuery === selectedPrompt?.query}
                  loading={loadingUpdate}
                />
                <Button
                  label="Delete"
                  icon="pi pi-trash"
                  className="p-button-outlined p-button-danger"
                  style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                  onClick={() => setDeleteDialogVisible(true)}
                  disabled={!selectedPrompt}
                />
              </div>
            </div>
            <div className="w-80 flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-gray-700">Prompt list ({prompts.length})</h3>
                <Button
                  label="Add prompt"
                  className="p-button-sm p-button-outlined"
                  onClick={() => setCreateDialogVisible(true)}
                />
              </div>
              <div className="flex flex-col gap-2 h-[500px] overflow-auto">
                {[...prompts].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()).map((prompt) => (
                  <div
                    key={prompt.id}
                    className={`p-3 rounded border cursor-pointer transition-all ${
                      selectedPrompt?.id === prompt.id
                        ? 'bg-blue-100 border-blue-500'
                        : prompt.is_active
                          ? 'bg-green-50 border-green-400'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                    onClick={() => setSelectedPrompt(prompt)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">
                        {prompt.version_name || `Prompt #${prompt.id}`}
                      </span>
                      {prompt.is_active && (
                        <span className="text-xs bg-green-700 text-white px-2 py-1 rounded-full font-semibold">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      last update: {new Date(prompt.updated_at).toLocaleString('cs-CZ')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog
        visible={dialogVisible}
        style={{ width: '95vw', height: '95vh' }}
        contentStyle={{ height: '100%' }}
        header="Prompt text"
        modal={true}
        onHide={() => setDialogVisible(false)}
        draggable={false}
        maximizable={true}
      >
        <div className="h-full overflow-auto bg-gray-50 p-4 rounded">
          <pre className="whitespace-pre-wrap text-sm font-mono text-gray-800">
            {selectedPrompt?.query}
          </pre>
        </div>
      </Dialog>

      <Dialog
        visible={createDialogVisible}
        style={{ width: '60vw', maxWidth: '800px' }}
        header="Vytvořit nový prompt"
        modal={true}
        onHide={() => setCreateDialogVisible(false)}
        draggable={false}
      >
        <div className="flex flex-col gap-4">
          <div className="field">
            <label className="font-bold block mb-2">Prompt type</label>
            <span className="text-lg bg-gray-100 px-3 py-2 rounded inline-block">
              {selectedPromptType}
            </span>
          </div>

          {isBasicType && (
            <>
              <div className="field">
                <label className="font-bold block mb-2">Charakter</label>
                <span className="text-lg bg-gray-100 px-3 py-2 rounded inline-block">
                  {selectedCharakter}
                </span>
              </div>

              <div className="field">
                <label className="font-bold block mb-2">HSOU</label>
                <span className="text-lg bg-gray-100 px-3 py-2 rounded inline-block">
                  {hsou ? 'Ano' : 'Ne'}
                </span>
              </div>
            </>
          )}

          <div className="field">
            <label htmlFor="versionName" className="font-bold block mb-2">
              Název verze
            </label>
            <InputText
              id="versionName"
              value={newPromptVersionName}
              onChange={(e) => setNewPromptVersionName(e.target.value)}
              placeholder="Zadejte název verze"
              className="w-full"
            />
          </div>

          <div className="field">
            <label htmlFor="newPromptQuery" className="font-bold block mb-2">
              Prompt text
            </label>
            <InputTextarea
              id="newPromptQuery"
              value={newPromptQuery}
              onChange={(e) => setNewPromptQuery(e.target.value)}
              placeholder="Zadejte text nového promptu"
              rows={10}
              className="w-full font-mono text-sm"
            />
          </div>

          <div className="field">
            <div className="flex items-center gap-2">
              <Checkbox
                inputId="newPromptIsActive"
                checked={newPromptIsActive}
                onChange={(e: CheckboxChangeEvent) => setNewPromptIsActive(e.checked ?? false)}
              />
              <label htmlFor="newPromptIsActive" className="font-bold cursor-pointer">
                Nastavit jako aktivní
              </label>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button
              label="Create"
              icon="pi pi-check"
              className="btn-primary"
              style={{ borderRadius: '6px' }}
              onClick={handleCreate}
              disabled={!newPromptQuery}
              loading={loadingCreate}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={deleteDialogVisible}
        style={{ width: '450px' }}
        header="Confirm deletion"
        modal={true}
        draggable={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Yes"
              className="p-button-outlined p-button-danger"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
              onClick={handleDelete}
              loading={loadingDelete}
            />
            <Button
              label="No"
              className="p-button-outlined"
              style={{ borderColor: '#9ca3af', color: '#9ca3af' }}
              onClick={() => setDeleteDialogVisible(false)}
            />
          </div>
        }
        onHide={() => setDeleteDialogVisible(false)}
      >
        <div className="flex items-center gap-3">
          <i className="pi pi-exclamation-triangle text-yellow-500 text-3xl" />
          <span>Are you sure you want to delete this prompt?</span>
        </div>
      </Dialog>

      <PageLoader visible={isInitialLoading} />
    </>
  );
};
